import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuCheck, LuHouse, LuRefreshCcw, LuTrophy, LuX } from "react-icons/lu";
import { PIECE } from "./utils/c4Logic";

export default function ConnectFourResultModal({
  status,
  winner,
  myPiece,
  p1Name,
  p2Name,
  isAI,
  onPlayAgain,
  rematchMessage = "",
  rematchShowActions = false,
  rematchPending = false,
  rematchButtonLocked = false,
  onAcceptRematch,
  onDeclineRematch,
}) {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  const isOpen = status === "won" || status === "draw";

  useEffect(() => {
    if (!isOpen) {
      setIsVisible(false);
      return undefined;
    }

    const id = window.setTimeout(() => setIsVisible(true), 1100);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  if (!isOpen || !isVisible) return null;

  const isDraw = status === "draw";
  const iWon = !isDraw && winner === myPiece;

  const headline = isDraw
    ? "It's a Draw!"
    : iWon
    ? "You Win!"
    : myPiece
    ? "You Lose"
    : winner === PIECE.ONE
    ? `${p1Name || "Player 1"} Wins!`
    : `${isAI ? "AI" : (p2Name || "Player 2")} Wins!`;

  const iconColor = isDraw
    ? "text-text-secondary"
    : iWon
    ? "text-[#C6A15B]"
    : "text-brand-rose";

  return (
    <div className="flex w-full max-w-md items-center justify-between gap-3 rounded-2xl border border-black/60 bg-[linear-gradient(145deg,#0b1117,#030607)] px-3 py-2.5 shadow-[0_18px_50px_rgba(0,0,0,0.48),inset_0_1px_0_rgba(255,255,255,0.055)]">
      <div className="flex min-w-0 items-center gap-2">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-border-soft bg-slate-950/45">
          {isDraw ? (
            <span className="h-2.5 w-2.5 rounded-full bg-text-secondary" />
          ) : (
            <LuTrophy size={17} className={iconColor} />
          )}
        </span>
        <div className="min-w-0">
          <h2 className="truncate font-display text-base font-semibold tracking-tight text-text-primary">{headline}</h2>
          <div className="text-[10px] uppercase tracking-[0.16em] text-text-faint">
            {rematchMessage ? (rematchShowActions ? rematchMessage : `${rematchMessage} waiting`) : "Game complete"}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {rematchShowActions ? (
          <>
            <button
              type="button"
              onClick={onAcceptRematch}
              disabled={rematchButtonLocked}
              aria-label="Accept rematch"
              title="Accept rematch"
              className="grid h-9 w-9 place-items-center rounded-xl border border-[#C6A15B]/35 bg-[#C6A15B]/12 text-[#FBBF24] transition hover:bg-[#C6A15B]/18 focus:outline-none disabled:cursor-not-allowed disabled:opacity-45"
            >
              <LuCheck size={17} />
            </button>
            <button
              type="button"
              onClick={onDeclineRematch}
              disabled={rematchButtonLocked}
              aria-label="Decline rematch"
              title="Decline rematch"
              className="grid h-9 w-9 place-items-center rounded-xl border border-brand-rose/30 bg-brand-rose/10 text-brand-rose transition hover:bg-brand-rose/15 focus:outline-none disabled:cursor-not-allowed disabled:opacity-45"
            >
              <LuX size={17} />
            </button>
          </>
        ) : onPlayAgain && !rematchPending ? (
          <button
            type="button"
            onClick={onPlayAgain}
            disabled={rematchButtonLocked}
            aria-label={isAI ? "Play again" : "Request rematch"}
            title={isAI ? "Play again" : "Request rematch"}
            className="grid h-9 w-9 place-items-center rounded-xl border border-[#C6A15B]/35 bg-[#C6A15B]/12 text-[#FBBF24] transition hover:bg-[#C6A15B]/18 focus:outline-none disabled:cursor-not-allowed disabled:opacity-45"
          >
            <LuRefreshCcw size={17} />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="Go home"
          title="Go home"
          className="grid h-9 w-9 place-items-center rounded-xl border border-border-soft bg-background-app-panel text-text-secondary transition hover:bg-surface-elevated hover:text-text-primary focus:outline-none"
        >
          <LuHouse size={17} />
        </button>
      </div>
    </div>
  );
}
